// LWZO (docs/02-konzept.md E27): Mehrfachauswahl mit Suche fuer Gruppen.
//
// Eine Gruppe ist im IdP nur ein String am Benutzer; es gibt keine
// Gruppenverwaltung. Die Auswahl bietet deshalb alles an, was ListGroups
// kennt (Mitgliedschaften aller Benutzer plus Gruppen aus der Konfiguration),
// und laesst daneben einen neuen Namen eintippen - das ist der einzige Weg,
// eine Gruppe "anzulegen". Der Eintrag dafuer steht sichtbar getrennt als
// "als neue Gruppe" und nie, wenn der Name schon existiert: so wird aus einem
// Tippfehler keine stille zweite Gruppe.
//
// Bedienung: tippen filtert; Pfeiltasten und Enter waehlen; Escape schliesst;
// Backspace im leeren Feld entfernt die letzte Gruppe; ein Klick daneben
// schliesst. Keine Abhaengigkeit, keine Portale - die Liste liegt absolut
// unter dem Feld.
import { mdiClose } from "@mdi/js";
import Icon from "@mdi/react";
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import styled from "styled-components/macro";
import { Chip, collator } from "./AdminUI";

const Box = styled.div<{ $focused: boolean; $disabled: boolean }>`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
  min-height: 42px;
  padding: 4px 8px;
  border: 2px solid ${(p) => (p.$focused ? "#009ee0" : "#949494")};
  border-radius: 2px;
  background: ${(p) => (p.$disabled ? "#f4f4f4" : "#fff")};
  cursor: text;
`;

const SearchInput = styled.input`
  flex: 1 1 120px;
  min-width: 120px;
  border: none;
  outline: none;
  padding: 6px 4px;
  font: inherit;
  background: transparent;
`;

const ChipRemove = styled.button`
  border: none;
  background: none;
  padding: 0;
  margin: 0;
  display: inline-flex;
  cursor: pointer;
  color: #666;
  :hover {
    color: #b84646;
  }
`;

const List = styled.ul`
  position: absolute;
  left: -2px;
  right: -2px;
  top: 100%;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  max-height: 240px;
  overflow-y: auto;
  background: #fff;
  border: 2px solid #009ee0;
  border-top: none;
  border-radius: 0 0 2px 2px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  z-index: 20;
`;

const Item = styled.li<{ $active: boolean; $create?: boolean }>`
  padding: 8px 12px;
  cursor: pointer;
  background: ${(p) => (p.$active ? "#e6f4fb" : "transparent")};
  font-style: ${(p) => (p.$create ? "italic" : "normal")};
  border-top: ${(p) => (p.$create ? "1px solid #ddd" : "none")};
`;

const Empty = styled.li`
  padding: 8px 12px;
  color: #666;
`;

type Choice = { name: string; create: boolean };

export function GroupSelect(props: {
  value: string[];
  options: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
}): JSX.Element {
  const { value, options, onChange, disabled } = props;
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const choices = useMemo<Choice[]>(() => {
    const q = query.trim();
    const qLower = q.toLowerCase();
    const selected = new Set(value);
    const known = new Set(options);
    const matching: Choice[] = options
      .filter((o) => !selected.has(o) && (qLower === "" || o.toLowerCase().includes(qLower)))
      .sort(collator.compare)
      .map((name) => ({ name, create: false }));
    // "Neu" nur, wenn es den Namen weder in der Liste noch in der Auswahl gibt.
    if (q !== "" && !known.has(q) && !selected.has(q)) {
      matching.push({ name: q, create: true });
    }
    return matching;
  }, [query, options, value]);

  useEffect(() => {
    if (active >= choices.length) {
      setActive(Math.max(0, choices.length - 1));
    }
  }, [choices, active]);

  // Klick daneben schliesst die Liste.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const pick = useCallback(
    (c: Choice) => {
      if (!value.includes(c.name)) {
        onChange([...value, c.name]);
      }
      setQuery("");
      setActive(0);
      inputRef.current?.focus();
    },
    [value, onChange]
  );

  const remove = useCallback(
    (name: string) => {
      onChange(value.filter((v) => v !== name));
    },
    [value, onChange]
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setOpen(true);
        setActive((a) => Math.min(a + 1, Math.max(0, choices.length - 1)));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActive((a) => Math.max(a - 1, 0));
        break;
      case "Enter":
        e.preventDefault();
        if (open && choices[active] !== undefined) {
          pick(choices[active]);
        } else {
          setOpen(true);
        }
        break;
      case "Escape":
        if (open) {
          e.preventDefault();
          setOpen(false);
        }
        break;
      case "Backspace":
        if (query === "" && value.length > 0) {
          remove(value[value.length - 1]);
        }
        break;
      default:
        break;
    }
  };

  return (
    <Box
      ref={boxRef}
      $focused={focused}
      $disabled={!!disabled}
      onClick={() => {
        if (!disabled) {
          inputRef.current?.focus();
          setOpen(true);
        }
      }}
    >
      {value.map((g) => (
        <Chip key={g}>
          {g}
          {!disabled && (
            <ChipRemove
              type="button"
              aria-label={`${g} entfernen`}
              title="Entfernen"
              onClick={(e) => {
                e.stopPropagation();
                remove(g);
              }}
            >
              <Icon size={0.55} path={mdiClose} />
            </ChipRemove>
          )}
        </Chip>
      ))}
      <SearchInput
        ref={inputRef}
        id={props.id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        autoComplete="off"
        disabled={disabled}
        value={query}
        placeholder={value.length === 0 ? props.placeholder ?? "Gruppe suchen oder neu eingeben" : ""}
        onChange={(e) => {
          setQuery(e.currentTarget.value);
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={onKeyDown}
      />
      {open && !disabled && (
        <List id={listId} role="listbox">
          {choices.length === 0 ? (
            <Empty>
              {options.length === 0 && value.length === 0
                ? "Noch keine Gruppen – Namen eintippen"
                : "Keine weitere Gruppe"}
            </Empty>
          ) : (
            choices.map((c, i) => (
              <Item
                key={(c.create ? "new:" : "opt:") + c.name}
                role="option"
                aria-selected={i === active}
                $active={i === active}
                $create={c.create}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => {
                  // mousedown statt click: der Fokus bleibt im Feld, die
                  // Liste geht nicht erst zu und wieder auf.
                  e.preventDefault();
                  pick(c);
                }}
              >
                {c.create ? `„${c.name}“ als neue Gruppe hinzufügen` : c.name}
              </Item>
            ))
          )}
        </List>
      )}
    </Box>
  );
}
